import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment-timezone';
import { toast } from 'sonner';
import useActivationPaymentSettings, {
    ActivationPaymentSettings,
    PaymentRecord,
    PaymentReportData,
} from '@/src/Activations/components/hooks/useActivationPaymentSettings';

/*
 * Payments — paid photo sessions for this event (ADMIN-MERGE Phase C;
 * classic's #payments tab). Stripe keys are write-only; the report
 * half renders on house primitives instead of classic's hand-rolled
 * div charts. The server gates writes and reports to admins — the
 * page shows an honest access note on 401.
 */

const RANGES = [
    { key: '7', label: '7 days' },
    { key: '30', label: '30 days' },
    { key: '90', label: '90 days' },
] as const;

const PAGE = 50;

const FIELD_LABEL =
    'mb-1.5 block text-[12px] font-semibold text-muted-foreground';

const dollars = (cents: number) =>
    (cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
    });

function Tile({
    label,
    value,
    sub,
}: {
    label: string;
    value: string;
    sub?: React.ReactNode;
}) {
    return (
        <div>
            <div className="text-[21px] font-bold leading-none tracking-[-0.01em] tabular-nums">
                {value}
            </div>
            <div className="mt-1.5 font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-faint">
                {label}
            </div>
            {sub && <div className="mt-1 text-[11px]">{sub}</div>}
        </div>
    );
}

export default function PaymentsV2() {
    const { activationId } = useParams();
    const id = activationId ? +activationId : 0;
    const navigate = useNavigate();
    const {
        getActivationPaymentSettings,
        createActivationPaymentSettings,
        updateActivationPaymentSettings,
        getPaymentReportData,
        getPaymentRecords,
    } = useActivationPaymentSettings();

    const [settings, setSettings] =
        useState<ActivationPaymentSettings | null>(null);
    const [denied, setDenied] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [draft, setDraft] = useState({
        cost: '',
        environment: 'test',
        publishableKey: '',
        secretKey: '',
    });
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    const [range, setRange] = useState<string>('30');
    const [custom, setCustom] = useState({ start: '', end: '' });
    const [report, setReport] = useState<PaymentReportData | null>(null);
    const [records, setRecords] = useState<PaymentRecord[]>([]);
    const [limit, setLimit] = useState(PAGE);

    const rangeDates = (): { start?: string; end?: string } => {
        if (range === 'custom') {
            return {
                start: custom.start || undefined,
                end: custom.end || undefined,
            };
        }
        return {
            start: moment()
                .subtract(+range, 'days')
                .format('YYYY-MM-DD'),
            end: moment().format('YYYY-MM-DD'),
        };
    };

    useEffect(() => {
        let mounted = true;
        getActivationPaymentSettings(id)
            .then((data) => {
                if (!mounted) return;
                setSettings(data);
                setDraft({
                    cost: data ? (data.costInCents / 100).toFixed(2) : '',
                    environment: data?.stripeEnvironment ?? 'test',
                    publishableKey: data?.stripePublishableKey ?? '',
                    secretKey: '',
                });
                setIsLoading(false);
            })
            .catch(() => {
                if (mounted) {
                    setDenied(true);
                    setIsLoading(false);
                }
            });
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!settings) return;
        if (range === 'custom' && (!custom.start || !custom.end)) return;
        let mounted = true;
        const { start, end } = rangeDates();
        Promise.all([
            getPaymentReportData(id, start, end).catch(() => null),
            getPaymentRecords(id, start, end).catch(() => []),
        ]).then(([reportData, recordRows]) => {
            if (!mounted) return;
            setReport(reportData);
            setRecords(recordRows ?? []);
            setLimit(PAGE);
        });
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings?.id, range, custom.start, custom.end]);

    const save = async () => {
        if (saving) return;
        const cents = Math.round(parseFloat(draft.cost) * 100);
        if (!Number.isFinite(cents) || cents <= 0) {
            setFormError('Cost per photo must be a positive amount.');
            return;
        }
        if (
            draft.publishableKey &&
            !/^pk_(test|live)_/.test(draft.publishableKey.trim())
        ) {
            setFormError('Publishable key should start with pk_test_ or pk_live_.');
            return;
        }
        if (draft.secretKey && !/^sk_/.test(draft.secretKey.trim())) {
            setFormError('Secret key should start with sk_.');
            return;
        }
        setSaving(true);
        setFormError(null);
        const body: any = {
            activationId: id,
            costInCents: cents,
            stripeEnvironment: draft.environment,
        };
        if (draft.publishableKey.trim())
            body.stripePublishableKey = draft.publishableKey.trim();
        if (draft.secretKey.trim())
            body.stripeSecretKey = draft.secretKey.trim();
        const result = settings
            ? await updateActivationPaymentSettings(body).catch(() => null)
            : await createActivationPaymentSettings(body).catch(() => null);
        setSaving(false);
        if (result) {
            setSettings(result);
            setDraft((d) => ({ ...d, secretKey: '' }));
            toast.success('Payment settings saved');
        } else {
            setFormError("Couldn't save — try again.");
        }
    };

    if (isLoading) {
        return (
            <main className="mx-auto max-w-[920px] px-[clamp(18px,3vw,34px)] pt-4">
                {[0, 1].map((i) => (
                    <div
                        key={i}
                        className="mb-4 h-20 animate-pulse rounded-[12px] bg-secondary"
                    />
                ))}
            </main>
        );
    }

    if (denied) {
        return (
            <main className="mx-auto max-w-[920px] px-[clamp(18px,3vw,34px)] pt-4">
                <div className="ps-label mb-2">Payments</div>
                <p className="text-[13px] text-muted-foreground">
                    Payments needs an admin account — ask an org admin for
                    access.
                </p>
            </main>
        );
    }

    const maxRevenue = Math.max(
        1,
        ...(report?.dailyChartData ?? []).map((d) => d.revenue),
    );
    const weekDelta =
        report && report.paymentsPreviousWeek > 0
            ? Math.round(
                  ((report.paymentsCurrentWeek -
                      report.paymentsPreviousWeek) /
                      report.paymentsPreviousWeek) *
                      100,
              )
            : null;

    return (
        <main className="mx-auto max-w-[920px] px-[clamp(18px,3vw,34px)] pt-4">
            <div className="ps-label mb-4">
                <button
                    type="button"
                    onClick={() => navigate(`/v2/activations/${id}`)}
                    className="ps-label cursor-pointer border-0 bg-transparent p-0 hover:text-foreground"
                >
                    Overview
                </button>
                <span className="mx-1.5 opacity-60">/</span>
                <span className="text-muted-foreground">Payments</span>
            </div>

            {/* settings */}
            <section className="mb-10 max-w-[520px]">
                <div className="mb-3 flex flex-wrap items-end gap-x-5 gap-y-3">
                    <div>
                        <label className={FIELD_LABEL}>
                            Cost per photo (USD)
                        </label>
                        <input
                            className="ps-input w-[110px] text-[13px] tabular-nums"
                            placeholder="5.00"
                            value={draft.cost}
                            onChange={(e) =>
                                setDraft((d) => ({
                                    ...d,
                                    cost: e.target.value,
                                }))
                            }
                        />
                    </div>
                    <div>
                        <label className={FIELD_LABEL}>Stripe</label>
                        <div className="flex gap-1">
                            {(['test', 'live'] as const).map((env) => (
                                <button
                                    key={env}
                                    type="button"
                                    onClick={() =>
                                        setDraft((d) => ({
                                            ...d,
                                            environment: env,
                                        }))
                                    }
                                    className={
                                        'cursor-pointer rounded-full border px-3 py-[5px] font-mono text-[10px] font-semibold uppercase tracking-[0.1em] ' +
                                        (draft.environment === env
                                            ? 'border-transparent bg-foreground text-background'
                                            : 'border-border bg-transparent text-muted-foreground')
                                    }
                                >
                                    {env}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <label className={FIELD_LABEL}>
                    Publishable key{' '}
                    <span className="font-normal text-faint">
                        — blank uses the system default
                    </span>
                </label>
                <input
                    className="ps-input mb-3 w-full font-mono text-[11.5px]"
                    placeholder="pk_test_…"
                    value={draft.publishableKey}
                    onChange={(e) =>
                        setDraft((d) => ({
                            ...d,
                            publishableKey: e.target.value,
                        }))
                    }
                />
                <label className={FIELD_LABEL}>Secret key</label>
                <input
                    type="password"
                    autoComplete="new-password"
                    className="ps-input mb-2 w-full font-mono text-[11.5px]"
                    placeholder={
                        settings?.hasStripeKeys
                            ? 'unchanged unless set'
                            : 'sk_test_…'
                    }
                    value={draft.secretKey}
                    onChange={(e) =>
                        setDraft((d) => ({
                            ...d,
                            secretKey: e.target.value,
                        }))
                    }
                />
                <p className="mb-3 text-[11.5px] text-faint">
                    {settings?.hasStripeKeys
                        ? 'Custom Stripe keys configured — never shown again after saving.'
                        : 'No custom keys — payments use the system Stripe account.'}
                </p>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        disabled={saving || !draft.cost.trim()}
                        onClick={save}
                        className="cursor-pointer rounded-full border-0 bg-brand px-5 py-2 font-[inherit] text-[13px] font-semibold text-brand-foreground disabled:opacity-50"
                    >
                        {saving
                            ? 'Saving…'
                            : settings
                            ? 'Update settings'
                            : 'Enable payments'}
                    </button>
                    {formError && (
                        <span className="text-[12px] font-semibold text-attention">
                            {formError}
                        </span>
                    )}
                </div>
            </section>

            {settings && (
                <>
                    <div className="mb-4 flex flex-wrap items-center gap-1.5">
                        {RANGES.map((r) => (
                            <button
                                key={r.key}
                                type="button"
                                onClick={() => setRange(r.key)}
                                className={
                                    'cursor-pointer rounded-full px-3 py-[5px] font-[inherit] text-[12px] font-semibold ' +
                                    (range === r.key
                                        ? 'border border-transparent bg-brand-soft text-brand-ink'
                                        : 'border border-border bg-transparent text-muted-foreground')
                                }
                            >
                                {r.label}
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={() => setRange('custom')}
                            className={
                                'cursor-pointer rounded-full px-3 py-[5px] font-[inherit] text-[12px] font-semibold ' +
                                (range === 'custom'
                                    ? 'border border-transparent bg-brand-soft text-brand-ink'
                                    : 'border border-border bg-transparent text-muted-foreground')
                            }
                        >
                            Custom
                        </button>
                        {range === 'custom' && (
                            <span className="flex items-center gap-1.5">
                                <input
                                    type="date"
                                    className="ps-input text-[12px]"
                                    value={custom.start}
                                    onChange={(e) =>
                                        setCustom((c) => ({
                                            ...c,
                                            start: e.target.value,
                                        }))
                                    }
                                />
                                <span className="text-faint">–</span>
                                <input
                                    type="date"
                                    className="ps-input text-[12px]"
                                    value={custom.end}
                                    onChange={(e) =>
                                        setCustom((c) => ({
                                            ...c,
                                            end: e.target.value,
                                        }))
                                    }
                                />
                            </span>
                        )}
                    </div>

                    {report && (
                        <>
                            <div className="mb-8 flex flex-wrap gap-9">
                                <Tile
                                    label="Last 24h"
                                    value={dollars(report.revenueLast24h)}
                                    sub={
                                        <span className="text-faint">
                                            {report.paymentsLast24h} payment
                                            {report.paymentsLast24h === 1
                                                ? ''
                                                : 's'}
                                        </span>
                                    }
                                />
                                <Tile
                                    label="This week"
                                    value={dollars(
                                        report.revenueCurrentWeek,
                                    )}
                                    sub={
                                        weekDelta != null ? (
                                            <span
                                                className={
                                                    weekDelta >= 0
                                                        ? 'font-semibold text-good'
                                                        : 'font-semibold text-attention'
                                                }
                                            >
                                                {weekDelta >= 0 ? '+' : ''}
                                                {weekDelta}% wow
                                            </span>
                                        ) : undefined
                                    }
                                />
                                <Tile
                                    label="This month"
                                    value={dollars(
                                        report.revenueCurrentMonth,
                                    )}
                                />
                                <Tile
                                    label="Lifetime"
                                    value={dollars(report.revenueLifetime)}
                                    sub={
                                        <span className="text-faint">
                                            {report.paymentsLifetime.toLocaleString()}{' '}
                                            payments
                                        </span>
                                    }
                                />
                                <Tile
                                    label="Sessions"
                                    value={report.totalSessions.toLocaleString()}
                                    sub={
                                        <span className="text-faint">
                                            {report.totalClaimed} claimed ·{' '}
                                            {report.totalInvalidated}{' '}
                                            invalidated
                                        </span>
                                    }
                                />
                            </div>

                            {report.dailyChartData.length > 1 && (
                                <div className="mb-8">
                                    <div className="ps-label mb-2">
                                        Revenue by day
                                    </div>
                                    <div className="flex h-[72px] items-end gap-[3px]">
                                        {report.dailyChartData.map(
                                            (day) => (
                                                <div
                                                    key={day.date}
                                                    title={`${moment(
                                                        day.date,
                                                    ).format(
                                                        'MMM D',
                                                    )} · ${dollars(
                                                        day.revenue,
                                                    )} · ${
                                                        day.count
                                                    } payments`}
                                                    className="min-w-[4px] flex-1 rounded-t-[3px] bg-brand"
                                                    style={{
                                                        height: `${Math.max(
                                                            3,
                                                            (day.revenue /
                                                                maxRevenue) *
                                                                100,
                                                        )}%`,
                                                        opacity:
                                                            day.revenue > 0
                                                                ? 1
                                                                : 0.25,
                                                    }}
                                                />
                                            ),
                                        )}
                                    </div>
                                    <div className="mt-1 flex justify-between font-mono text-[9px] uppercase tracking-[0.1em] text-faint">
                                        <span>
                                            {moment(
                                                report.dailyChartData[0]
                                                    .date,
                                            ).format('MMM D')}
                                        </span>
                                        <span>
                                            {moment(
                                                report.dailyChartData[
                                                    report.dailyChartData
                                                        .length - 1
                                                ].date,
                                            ).format('MMM D')}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    <section>
                        <div className="ps-label mb-2">
                            Payments · {records.length.toLocaleString()}
                        </div>
                        {records.length === 0 && (
                            <p className="py-2 text-[13px] text-muted-foreground">
                                No payments in this range.
                            </p>
                        )}
                        {records.slice(0, limit).map((record) => (
                            <div
                                key={record.id}
                                className="flex items-center gap-3 border-b border-line py-2 text-[12.5px] last:border-b-0"
                            >
                                <span className="w-[76px] flex-none font-semibold tabular-nums">
                                    {dollars(record.costInCents)}
                                </span>
                                <span className="min-w-0 flex-1 truncate font-mono text-[10px] text-faint">
                                    {record.photoSessionId}
                                </span>
                                <span className="hidden font-mono text-[10px] uppercase text-faint sm:block">
                                    {record.redemptionCode}
                                </span>
                                <span
                                    className={
                                        'text-[11px] font-semibold ' +
                                        (record.invalidatedAt
                                            ? 'text-attention'
                                            : record.claimedAt
                                            ? 'text-good'
                                            : 'text-muted-foreground')
                                    }
                                >
                                    {record.invalidatedAt
                                        ? 'invalidated'
                                        : record.claimedAt
                                        ? 'claimed'
                                        : 'paid'}
                                </span>
                                <span className="whitespace-nowrap text-right text-[11.5px] text-faint">
                                    {moment(record.paidAt).format(
                                        'MMM D, h:mm A',
                                    )}
                                </span>
                            </div>
                        ))}
                        {records.length > limit && (
                            <div className="flex items-center justify-between border-t border-line py-3">
                                <span className="text-[12px] text-faint">
                                    {limit} of {records.length}
                                </span>
                                <button
                                    type="button"
                                    onClick={() =>
                                        setLimit((l) => l + PAGE)
                                    }
                                    className="ps-pill cursor-pointer px-4 font-[inherit] text-[12.5px] font-semibold text-foreground"
                                >
                                    Show more
                                </button>
                            </div>
                        )}
                    </section>
                </>
            )}
        </main>
    );
}
